import { motion } from "framer-motion";
import { MessageSquare, MapPin, PenTool, Hammer, Sofa, KeyRound } from "lucide-react";

const STEPS = [
    { icon: MessageSquare, title: "Free Consultation", desc: "Share your vision, plot details & budget. We listen first." },
    { icon: MapPin, title: "Site Inspection", desc: "We visit, measure, study soil and Vastu — at no cost." },
    { icon: PenTool, title: "Planning & Design", desc: "2D layouts, 3D elevations, BoQ & timeline locked." },
    { icon: Hammer, title: "Construction Execution", desc: "Quality materials, daily logs, transparent billing." },
    { icon: Sofa, title: "Interior & Finishing", desc: "Modular interiors, premium finishes, smart wiring." },
    { icon: KeyRound, title: "Final Handover", desc: "Snag-list resolved, keys handed over, housewarming ready." },
];

export default function Process() {
    return (
        <section id="process" className="relative bg-white py-20 md:py-32 overflow-hidden" data-testid="process-section">
            <div className="max-w-7xl mx-auto px-5 md:px-10">
                <div className="text-center max-w-2xl mx-auto mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="inline-flex items-center gap-3 mb-6"
                    >
                        <span className="w-8 h-px bg-brand-orange" />
                        <span className="text-[11px] uppercase tracking-[0.25em] text-brand-orange font-medium">Our Process</span>
                        <span className="w-8 h-px bg-brand-orange" />
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="font-display font-semibold text-brand-ink text-3xl md:text-4xl lg:text-[44px] leading-[1.05] tracking-tight"
                        data-testid="process-heading"
                    >
                        From blueprint to <span className="font-serif-italic font-normal brand-gradient-text">housewarming.</span>
                    </motion.h2>
                </div>

                {/* Vertical timeline (mobile) / Horizontal (desktop) */}
                <div className="relative">
                    {/* Connector line desktop */}
                    <div className="hidden lg:block absolute top-12 left-12 right-12 h-px bg-gradient-to-r from-transparent via-brand-line to-transparent" />

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-4">
                        {STEPS.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <motion.div
                                    key={s.title}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-50px" }}
                                    transition={{ duration: 0.6, delay: i * 0.1 }}
                                    className="group relative text-center lg:text-left"
                                    data-testid={`process-step-${i}`}
                                >
                                    <div className="relative inline-flex">
                                        <motion.div
                                            whileHover={{ scale: 1.03 }}
                                            transition={{ type: "spring", stiffness: 300 }}
                                            className="relative w-20 h-20 rounded-full bg-white border border-brand-line flex items-center justify-center group-hover:brand-gradient group-hover:border-transparent transition-all duration-500 z-10"
                                        >
                                            <Icon size={24} strokeWidth={1.6} className="text-brand-ink group-hover:text-white transition-colors" />
                                        </motion.div>
                                        <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-brand-ink text-white text-[10px] font-medium tabular-nums flex items-center justify-center z-20">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                    </div>
                                    <h3 className="font-display font-semibold text-base md:text-lg text-brand-ink mt-4 tracking-tight">
                                        {s.title}
                                    </h3>
                                    <p className="text-[13px] text-brand-muted leading-relaxed mt-1.5 max-w-[200px] mx-auto lg:mx-0">
                                        {s.desc}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}
